import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import React from 'react'

import {
  createCashHqDeposit,
  fetchCashHqSafe,
  fetchCashHqSafeDeposits,
  receiveCashAtHq
} from 'api/cashRegister'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import NoteCounter, {
  NoteSummary,
  NoteTally,
  anyCounted,
  noteTotal,
  tallyRows,
  toEditable,
  toPayload
} from './NoteCounter'

// HQ Finance's own drawer.
//
// Until this existed the register stopped at the centre's front door: a
// handover marked the centre's row settled and the cash left the system.
// Nothing could answer "what is HQ holding" — in one February the old sheet
// says ₹2,43,064 across twenty envelopes, and none of it was in Alfred.
//
// Three states, in the order money passes through them:
//   To receive  a centre sent it; nobody at HQ has opened it yet
//   In the safe opened, counted, still here
//   Movements   what left — a bank run, or cash handed out for a payment
//
// ⭐ Receiving IS counting. The second count is the only check anybody
// performs on this money, and seven of the old sheet's 49 counted handovers
// disagreed with the centre. A difference does not block the receipt; it has
// to carry a reason.
const useStyles = makeStyles((theme) => ({
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  kpi: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(1.5)
  },
  kpiHot: { borderColor: '#f4c7c3', background: '#fdf3f2' },
  kpiValue: {
    fontSize: 21,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  kpiLabel: { fontSize: 12, color: '#777' },
  strip: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  stripHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  section: { marginTop: theme.spacing(3) },
  sectionHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  amount: { fontVariantNumeric: 'tabular-nums' },
  declared: { fontVariantNumeric: 'tabular-nums', color: '#8a8175' },
  countRow: { background: '#fafafa', borderLeft: '3px solid #F25E35' },
  countHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  reasonBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    alignItems: 'flex-start'
  },
  reasonField: { minWidth: 280 },
  noteField: { minWidth: 280 },
  card: { padding: theme.spacing(1.5), marginBottom: theme.spacing(1.5) },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2)
  },
  cardMeta: { color: '#777', fontSize: 13, marginTop: 2 },
  entryLine: { fontSize: 13, color: '#555' },
  short: { color: '#a32b21' },
  matches: { color: '#1b6e52' },
  placeholder: { color: '#9e9e9e' }
}))

// LOCAL PREVIEW ONLY (vite dev server; never true in any build): synthetic
// data on fetch failure, so the screen can be walked through before the
// endpoints reach the dev API. Every figure is a real row from the old HQ
// sheet.
const SAMPLE_SAFE = {
  denominations: [500, 200, 100, 50, 20, 10, 5, 2, 1],
  reasons: [
    {
      value: 'change_returned',
      label: 'Change handed back to the parent',
      is_shortfall: false
    },
    {
      value: 'change_received',
      label: 'Change taken from another collection',
      is_shortfall: false
    },
    {
      value: 'coins',
      label: 'Coins the centre had not counted',
      is_shortfall: false
    },
    {
      value: 'centre_miscount',
      label: 'The centre miscounted',
      is_shortfall: false
    },
    {
      value: 'short',
      label: 'Cash was short — being followed up',
      is_shortfall: true
    },
    { value: 'other', label: 'Something else', is_shortfall: false }
  ],
  kinds: [
    { value: 'bank', label: 'Deposited it in the bank' },
    { value: 'handout', label: 'Gave it to someone for a payment' }
  ],
  kpis: {
    in_transit_count: 2,
    in_transit_amount: 45312,
    held_count: 2,
    held_amount: 13540,
    handed_out_amount: 0,
    balance_amount: 13540
  },
  // The sum of the two held envelopes below, to the rupee — sample data that
  // does not add up teaches the reader to distrust the real thing.
  note_balance: {
    rows: [
      { denomination: 500, count: 19, value: 9500 },
      { denomination: 200, count: 20, value: 4000 },
      { denomination: 100, count: 0, value: 0 },
      { denomination: 50, count: 0, value: 0 },
      { denomination: 20, count: 2, value: 40 },
      { denomination: 10, count: 0, value: 0 },
      { denomination: 5, count: 0, value: 0 },
      { denomination: 2, count: 0, value: 0 },
      { denomination: 1, count: 0, value: 0 }
    ],
    total: 13540
  },
  in_transit: [
    {
      uuid: 't1',
      payer: 'Nidhi Jain · Preschool',
      parent_phone: '+91 98450 39010',
      centre: 'Indiranagar',
      collected_on: '2026-08-26',
      collected_by: 'Sonal Madliya',
      declared_amount: 38232,
      declared_counts: {
        500: 76,
        200: 1,
        100: 0,
        50: 0,
        20: 1,
        10: 0,
        5: 0,
        2: 6,
        1: 0
      },
      declared_counted: true,
      sent_on: '2026-08-28',
      sent_by: 'Sonal Madliya',
      sent_to: 'Madhurya'
    },
    {
      uuid: 't2',
      payer: 'Nidhi Jain · After School',
      parent_phone: '+91 98450 39010',
      centre: 'Indiranagar',
      collected_on: '2026-08-26',
      collected_by: 'Sonal Madliya',
      declared_amount: 7080,
      declared_counts: {
        500: 14,
        200: 0,
        100: 1,
        50: 0,
        20: 0,
        10: 0,
        5: 0,
        2: 0,
        1: 0
      },
      declared_counted: true,
      sent_on: '2026-08-28',
      sent_by: 'Sonal Madliya',
      sent_to: 'Madhurya'
    }
  ],
  held: [
    {
      uuid: 'h1',
      pay_in: 'p1',
      payer: 'Halima Sadya · Chess',
      centre: 'Indiranagar',
      received_on: '2026-08-28',
      received_by: 'Madhurya',
      received_from: 'Sonal Madliya',
      declared_amount: 10020,
      counted_total: 10020,
      difference: 0,
      reason: '',
      reason_label: '',
      note: '',
      counts: {
        500: 12,
        200: 20,
        100: 0,
        50: 0,
        20: 1,
        10: 0,
        5: 0,
        2: 0,
        1: 0
      }
    },
    {
      uuid: 'h2',
      payer: 'Sonali Saha · After School',
      centre: 'Indiranagar',
      received_on: '2026-08-28',
      received_by: 'Madhurya',
      received_from: 'Sonal Madliya',
      declared_amount: 3540,
      counted_total: 3520,
      difference: -20,
      reason: 'change_returned',
      reason_label: 'Change handed back to the parent',
      note: '₹20 change given to Nidhi Jain',
      counts: { 500: 7, 200: 0, 100: 0, 50: 0, 20: 1, 10: 0, 5: 0, 2: 0, 1: 0 }
    }
  ]
}
const SAMPLE_MOVEMENTS = {
  count: 1,
  results: [
    {
      uuid: 'm1',
      kind: 'bank',
      destination: 'HDFC 50200046242256',
      bank: 'HDFC 50200046242256',
      given_to: '',
      purpose: '',
      moved_on: '2026-08-22',
      recorded_by: 'Madhurya',
      amount: 103566,
      note: '',
      note_tally: {
        rows: [
          { denomination: 500, count: 206, value: 103000 },
          { denomination: 200, count: 0, value: 0 },
          { denomination: 100, count: 5, value: 500 },
          { denomination: 50, count: 1, value: 50 },
          { denomination: 10, count: 1, value: 10 },
          { denomination: 5, count: 1, value: 5 },
          { denomination: 1, count: 1, value: 1 }
        ],
        total: 103566
      },
      entries: [
        {
          payer: 'Mithal Rathod · Preschool',
          amount: 103566,
          received_on: '2026-08-05'
        }
      ],
      challans: [{ uuid: 'c1', name: 'hdfc_challan_2208.pdf', url: '#' }]
    }
  ]
}

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`
const signedInr = (n) =>
  `${Number(n) > 0 ? '+' : Number(n) < 0 ? '−' : ''}${inr(
    Math.abs(Number(n) || 0)
  )}`

const today = () => Dayjs().format('YYYY-MM-DD')

const Safe = () => {
  const classes = useStyles()
  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState(null)
  const [movements, setMovements] = React.useState(null)
  const [error, setError] = React.useState('')

  // ---- receiving one envelope --------------------------------------------
  const [receiving, setReceiving] = React.useState(null)
  const [savingRow, setSavingRow] = React.useState(false)

  // ---- banking out of the safe -------------------------------------------
  const [selected, setSelected] = React.useState([])
  const [movement, setMovement] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const fileInputRef = React.useRef(null)

  const load = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    setError('')
    try {
      const [safe, history] = await Promise.all([
        fetchCashHqSafe(),
        fetchCashHqSafeDeposits()
      ])
      setData(safe)
      setMovements(history)
    } catch (e) {
      if (import.meta.env.DEV) {
        setData(SAMPLE_SAFE)
        setMovements(SAMPLE_MOVEMENTS)
      } else {
        setError(
          e?.response?.data?.detail ||
            'Could not load the HQ safe. Please try again.'
        )
      }
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const denominations = data?.denominations || [
    500, 200, 100, 50, 20, 10, 5, 2, 1
  ]
  const reasons = data?.reasons || []
  const kpis = data?.kpis || {}
  const inTransit = data?.in_transit || []
  const held = data?.held || []

  // ---- receive ------------------------------------------------------------
  const startReceive = (row) => {
    if (savingRow) return
    setReceiving({
      payInId: row.uuid,
      payer: row.payer,
      declared: Number(row.declared_amount || 0),
      // Pre-fill with what the centre counted. HQ is checking a bundle, not
      // starting from nothing — and where the two agree, that is one click
      // instead of nine.
      counts: toEditable(row.declared_counts, denominations),
      receivedOn: today(),
      receivedFrom: row.sent_by || '',
      reason: '',
      note: ''
    })
  }

  const receiveTotal = receiving
    ? noteTotal(receiving.counts, denominations)
    : 0
  const receiveDifference = receiving ? receiveTotal - receiving.declared : 0
  const receiveCounted = receiving
    ? anyCounted(receiving.counts, denominations)
    : false
  const receiveReasonMeta = reasons.find((x) => x.value === receiving?.reason)
  const receiveNeedsReason =
    !!receiving && receiveDifference !== 0 && !receiving.reason
  const receiveNeedsNote =
    !!receiving &&
    !!receiveReasonMeta &&
    (receiving.reason === 'other' || receiveReasonMeta.is_shortfall) &&
    !receiving.note.trim()
  const receiveReady =
    !!receiving && receiveCounted && !receiveNeedsReason && !receiveNeedsNote

  const submitReceive = async () => {
    if (!receiveReady || savingRow) return
    setSavingRow(true)
    try {
      await receiveCashAtHq({
        payInId: receiving.payInId,
        counts: toPayload(receiving.counts, denominations),
        receivedOn: receiving.receivedOn,
        receivedFrom: receiving.receivedFrom.trim(),
        reason: receiveDifference === 0 ? '' : receiving.reason,
        note: receiving.note.trim()
      })
      eventAlert(
        `Received ${inr(receiveTotal)} — it is in the safe.`,
        constants.SUCCESS,
        'Cash Register'
      )
      setReceiving(null)
      await load({ silent: true })
    } catch (e) {
      eventAlert(
        e.message || 'Could not receive that envelope.',
        constants.ERROR,
        'Cash Register'
      )
    } finally {
      setSavingRow(false)
    }
  }

  // ---- move cash out ------------------------------------------------------
  const isSelected = (r) => selected.includes(r.uuid)
  const toggleSelect = (r) =>
    setSelected((prev) =>
      prev.includes(r.uuid)
        ? prev.filter((x) => x !== r.uuid)
        : [...prev, r.uuid]
    )
  const selectedRows = held.filter(isSelected)
  const selectedAmount = selectedRows.reduce(
    (sum, r) => sum + Number(r.counted_total || 0),
    0
  )

  // Both movements are opened the same way. What differs is whether they name
  // envelopes: a bank run always does — you cannot deposit money that came
  // from nowhere — while a handout does so only if envelopes happened to be
  // ticked. Cash for the GST payment usually comes off the top of the pile,
  // and then the note count is the only record there is.
  const openMovement = (kind) => {
    const tally = {}
    selectedRows.forEach((r) =>
      denominations.forEach((d) => {
        tally[String(d)] =
          (tally[String(d)] || 0) + Number(r.counts?.[String(d)] || 0)
      })
    )
    setMovement({
      kind,
      bank: '',
      givenTo: '',
      purpose: '',
      movedOn: today(),
      note: '',
      // The bag starts as the sum of the envelopes going in. It stays editable
      // because HQ genuinely does break a ₹500 into change between counting
      // and banking, and the slip has to say what physically went in.
      counts: toEditable(selectedRows.length ? tally : {}, denominations),
      receiptIds: selectedRows.map((r) => r.uuid),
      envelopeTotal: selectedAmount,
      file: null
    })
  }

  const movementTotal = movement ? noteTotal(movement.counts, denominations) : 0
  const movementCovers = !!movement && movement.receiptIds.length > 0
  // Whatever shape the bundle is in, it has to come to the same money as the
  // envelopes it empties.
  const movementBalances =
    !movementCovers || movementTotal === Math.round(movement.envelopeTotal)
  const movementReady =
    !!movement &&
    movementBalances &&
    (movement.kind === 'bank'
      ? movement.bank.trim() && movement.file && movementCovers
      : movement.givenTo.trim() &&
        movement.purpose.trim() &&
        anyCounted(movement.counts, denominations))

  const submitMovement = async () => {
    if (!movementReady || saving) return
    setSaving(true)
    try {
      const result = await createCashHqDeposit({
        kind: movement.kind,
        bank: movement.bank.trim(),
        givenTo: movement.givenTo.trim(),
        purpose: movement.purpose.trim(),
        movedOn: movement.movedOn,
        note: movement.note.trim(),
        receiptIds: movement.receiptIds,
        counts: toPayload(movement.counts, denominations),
        file: movement.file
      })
      eventAlert(
        `${inr(result.amount)} recorded — ${result.destination}.`,
        constants.SUCCESS,
        'Cash Register'
      )
      setMovement(null)
      setSelected([])
      await load({ silent: true })
    } catch (e) {
      eventAlert(
        e.message || 'Could not record that movement.',
        constants.ERROR,
        'Cash Register'
      )
    } finally {
      setSaving(false)
    }
  }

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  if (error)
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
        <Button size="small" onClick={() => load()} style={{ marginTop: 8 }}>
          Retry
        </Button>
      </Box>
    )

  return (
    <>
      <div className={classes.kpis}>
        <div
          className={`${classes.kpi} ${
            kpis.in_transit_count ? classes.kpiHot : ''
          }`}
        >
          <div className={classes.kpiValue}>{kpis.in_transit_count || 0}</div>
          <div className={classes.kpiLabel}>
            To receive · {inr(kpis.in_transit_amount)}
          </div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiValue}>{inr(kpis.held_amount)}</div>
          <div className={classes.kpiLabel}>
            In the safe · {kpis.held_count || 0} envelope
            {kpis.held_count === 1 ? '' : 's'}
          </div>
        </div>
        {kpis.handed_out_amount > 0 && (
          <div className={classes.kpi}>
            <div className={classes.kpiValue}>
              {inr(kpis.handed_out_amount)}
            </div>
            <div className={classes.kpiLabel}>Handed out for payments</div>
          </div>
        )}
        <div className={classes.kpi}>
          <div className={classes.kpiValue}>{inr(kpis.balance_amount)}</div>
          <div className={classes.kpiLabel}>Should be in the drawer</div>
        </div>
      </div>

      {/* The nine numbers the drawer should physically match, and the ones a
          pay-in slip asks for. */}
      <div className={classes.strip}>
        <div className={classes.stripHead}>
          <Typography variant="subtitle2">Notes in the drawer</Typography>
          <Typography variant="caption" color="textSecondary">
            Envelopes held, less anything handed out. Count the drawer against
            this.
          </Typography>
        </div>
        <NoteTally rows={data?.note_balance?.rows || []} />
      </div>

      {/* ---- to receive ---- */}
      <div className={classes.section}>
        <div className={classes.sectionHead}>
          <Typography variant="subtitle1">
            <b>To receive</b>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Sent by a centre, not opened yet. Receiving an envelope means
            counting it — that second count is the only check on this money.
          </Typography>
        </div>
        {!inTransit.length ? (
          <Typography variant="body2" color="textSecondary">
            Nothing on its way. Centres send cash here by settling with{' '}
            <b>Sent to HQ Finance</b>.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sent</TableCell>
                <TableCell>Centre</TableCell>
                <TableCell>Parent / Order</TableCell>
                <TableCell align="right">Centre says</TableCell>
                <TableCell>Counted at the centre</TableCell>
                <TableCell>Sent by</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {inTransit.map((r) => {
                const open = receiving?.payInId === r.uuid
                return (
                  <React.Fragment key={r.uuid}>
                    <TableRow hover>
                      <TableCell>
                        {r.sent_on ? Dayjs(r.sent_on).format('D MMM') : '—'}
                      </TableCell>
                      <TableCell>{r.centre || '—'}</TableCell>
                      <TableCell>{r.payer}</TableCell>
                      <TableCell align="right" className={classes.amount}>
                        {inr(r.declared_amount)}
                      </TableCell>
                      <TableCell>
                        {r.declared_counted ? (
                          <NoteSummary
                            rows={tallyRows(r.declared_counts, denominations)}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            className={classes.short}
                          >
                            Not counted at the centre
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{r.sent_by || r.sent_to || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={open ? 'text' : 'outlined'}
                          disabled={savingRow}
                          onClick={() =>
                            open ? setReceiving(null) : startReceive(r)
                          }
                        >
                          {open ? 'Cancel' : 'Receive & count'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className={classes.countRow}>
                        <TableCell colSpan={7}>
                          <div className={classes.countHead}>
                            <Typography variant="subtitle2">
                              Count {receiving.payer.split(' · ')[0]}&rsquo;s
                              envelope
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Pre-filled with the centre&rsquo;s own count —
                              change whatever is actually on the table.
                            </Typography>
                          </div>
                          <NoteCounter
                            denominations={denominations}
                            counts={receiving.counts}
                            disabled={savingRow}
                            target={receiving.declared}
                            targetLabel="what the centre sent"
                            totalLabel="You counted"
                            onChange={(next) =>
                              setReceiving((prev) => ({
                                ...prev,
                                counts: next
                              }))
                            }
                          />
                          <div className={classes.reasonBox}>
                            <TextField
                              size="small"
                              variant="outlined"
                              type="date"
                              label="Received on"
                              value={receiving.receivedOn}
                              disabled={savingRow}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ max: today() }}
                              onChange={(e) => {
                                const next = e.target.value
                                setReceiving((prev) => ({
                                  ...prev,
                                  receivedOn: next
                                }))
                              }}
                            />
                            <TextField
                              size="small"
                              variant="outlined"
                              label="Handed over by"
                              placeholder="e.g. Sonal Madliya"
                              value={receiving.receivedFrom}
                              disabled={savingRow}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) => {
                                const next = e.target.value
                                setReceiving((prev) => ({
                                  ...prev,
                                  receivedFrom: next
                                }))
                              }}
                            />
                            {receiveDifference !== 0 && (
                              <TextField
                                select
                                size="small"
                                variant="outlined"
                                className={classes.reasonField}
                                label={`Why ${signedInr(receiveDifference)}?`}
                                value={receiving.reason}
                                disabled={savingRow}
                                error={receiveNeedsReason}
                                // An empty MUI select floats its label INSIDE
                                // the box, so the question reads as an answer
                                // and the disabled button looks broken.
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                  displayEmpty: true,
                                  renderValue: (value) =>
                                    value ? (
                                      reasons.find((x) => x.value === value)
                                        ?.label
                                    ) : (
                                      <span className={classes.placeholder}>
                                        Select a reason…
                                      </span>
                                    )
                                }}
                                onChange={(e) => {
                                  const next = e.target.value
                                  setReceiving((prev) => ({
                                    ...prev,
                                    reason: next
                                  }))
                                }}
                              >
                                {reasons.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                            <TextField
                              size="small"
                              variant="outlined"
                              className={classes.noteField}
                              label="Note"
                              placeholder={
                                receiveNeedsNote
                                  ? 'What happened? (required)'
                                  : 'Optional'
                              }
                              value={receiving.note}
                              disabled={savingRow}
                              error={receiveNeedsNote}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) => {
                                const next = e.target.value
                                setReceiving((prev) => ({
                                  ...prev,
                                  note: next
                                }))
                              }}
                            />
                          </div>
                          <Box mt={1.5} display="flex" alignItems="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              disabled={!receiveReady || savingRow}
                              onClick={submitReceive}
                            >
                              {savingRow
                                ? 'Saving…'
                                : `Take ${inr(receiveTotal)} into the safe`}
                            </Button>
                            {!receiveReady && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ marginLeft: 12 }}
                              >
                                {!receiveCounted
                                  ? 'Count the notes first.'
                                  : receiveNeedsReason
                                  ? 'Pick why your count differs.'
                                  : 'Add a short note saying what happened.'}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ---- in the safe ---- */}
      <div className={classes.section}>
        <div className={classes.sectionHead}>
          <Typography variant="subtitle1">
            <b>In the safe</b>
          </Typography>
          {selected.length > 0 ? (
            <>
              <Typography variant="caption" color="textSecondary">
                {selected.length} selected · {inr(selectedAmount)}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => openMovement('bank')}
              >
                Take it to the bank
              </Button>
            </>
          ) : (
            <Typography variant="caption" color="textSecondary">
              Counted and held here. Tick the ones you are taking to the bank.
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            style={{ marginLeft: 'auto' }}
            onClick={() => openMovement('handout')}
          >
            {selected.length
              ? 'Give the ticked cash to someone'
              : 'Give cash to someone'}
          </Button>
        </div>
        {!held.length ? (
          <Typography variant="body2" color="textSecondary">
            The safe is empty — nothing received from a centre is waiting here.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Received</TableCell>
                <TableCell>Centre</TableCell>
                <TableCell>Parent / Order</TableCell>
                <TableCell align="right">Centre said</TableCell>
                <TableCell align="right">You counted</TableCell>
                <TableCell>Difference</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {held.map((r) => (
                <TableRow key={r.uuid} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isSelected(r)}
                      onChange={() => toggleSelect(r)}
                    />
                  </TableCell>
                  <TableCell>{Dayjs(r.received_on).format('D MMM')}</TableCell>
                  <TableCell>{r.centre || '—'}</TableCell>
                  <TableCell>{r.payer}</TableCell>
                  <TableCell align="right" className={classes.declared}>
                    {inr(r.declared_amount)}
                  </TableCell>
                  <TableCell align="right" className={classes.amount}>
                    {inr(r.counted_total)}
                  </TableCell>
                  <TableCell>
                    {r.difference ? (
                      <Chip
                        size="small"
                        label={`${r.reason_label || 'Difference'} · ${signedInr(
                          r.difference
                        )}`}
                        style={{ background: '#fdecea', color: '#a32b21' }}
                      />
                    ) : (
                      <Typography variant="caption" className={classes.matches}>
                        Matches
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <NoteSummary rows={tallyRows(r.counts, denominations)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ---- what has left ---- */}
      <div className={classes.section}>
        <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
          <b>What has left the safe</b>
        </Typography>
        {!movements?.results?.length ? (
          <Typography variant="body2" color="textSecondary">
            Nothing has left the safe yet — no bank runs, nothing handed out.
          </Typography>
        ) : (
          movements.results.map((m) => (
            <Paper key={m.uuid} variant="outlined" className={classes.card}>
              <div className={classes.cardTop}>
                <div>
                  <Typography variant="subtitle2">{m.destination}</Typography>
                  <div className={classes.cardMeta}>
                    {Dayjs(m.moved_on).format('D MMM YYYY')}
                    {m.recorded_by ? ` · ${m.recorded_by}` : ''}
                    {m.entries.length
                      ? ` · ${m.entries.length} envelope${
                          m.entries.length === 1 ? '' : 's'
                        }`
                      : ' · cash off the pile, against no particular collection'}
                    {m.challans.map((c) => (
                      <React.Fragment key={c.uuid}>
                        {' · '}
                        <a
                          href={c.view_url || c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {c.name}
                        </a>
                      </React.Fragment>
                    ))}
                    {m.note ? ` · ${m.note}` : ''}
                  </div>
                </div>
                <Typography variant="subtitle2" className={classes.amount}>
                  {inr(m.amount)}
                </Typography>
              </div>
              <Box mt={0.75}>
                <NoteTally rows={m.note_tally?.rows || []} />
              </Box>
              {m.entries.map((e, i) => (
                <div key={i} className={classes.entryLine}>
                  {Dayjs(e.received_on).format('D MMM')} · {e.payer} ·{' '}
                  {inr(e.amount)}
                </div>
              ))}
            </Paper>
          ))
        )}
      </div>

      {/* ---- one movement out ---- */}
      <Dialog
        open={!!movement}
        onClose={() => !saving && setMovement(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {movement?.kind === 'bank'
            ? 'Take it to the bank'
            : 'Give cash out for a payment'}
        </DialogTitle>
        <DialogContent>
          {movement?.kind === 'bank' ? (
            <>
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                {movement.receiptIds.length} envelope
                {movement.receiptIds.length === 1 ? '' : 's'} ·{' '}
                <b>{inr(movement.envelopeTotal)}</b>
              </Typography>
              <TextField
                label="Bank / account"
                placeholder="e.g. HDFC 50200046242256"
                fullWidth
                size="small"
                variant="outlined"
                value={movement.bank}
                onChange={(e) => {
                  const next = e.target.value
                  setMovement((prev) => ({ ...prev, bank: next }))
                }}
                style={{ marginBottom: 12 }}
              />
            </>
          ) : (
            movement && (
              <>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 12 }}
                >
                  {movement.receiptIds.length
                    ? `Covering the ${movement.receiptIds.length} envelope${
                        movement.receiptIds.length === 1 ? '' : 's'
                      } you ticked · ${inr(movement.envelopeTotal)}.`
                    : 'Cash off the top of the pile, against no particular envelope — the note count below is the only record of it.'}
                </Typography>
                <TextField
                  label="Given to"
                  placeholder="e.g. Satya"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={movement.givenTo}
                  onChange={(e) => {
                    const next = e.target.value
                    setMovement((prev) => ({ ...prev, givenTo: next }))
                  }}
                  style={{ marginBottom: 12 }}
                />
                <TextField
                  label="What for"
                  placeholder="e.g. GST payment"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={movement.purpose}
                  onChange={(e) => {
                    const next = e.target.value
                    setMovement((prev) => ({ ...prev, purpose: next }))
                  }}
                  style={{ marginBottom: 12 }}
                />
              </>
            )
          )}
          {movement && (
            <>
              <TextField
                label={movement.kind === 'bank' ? 'Deposited on' : 'Given on'}
                type="date"
                fullWidth
                size="small"
                variant="outlined"
                value={movement.movedOn}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today() }}
                onChange={(e) => {
                  const next = e.target.value
                  setMovement((prev) => ({ ...prev, movedOn: next }))
                }}
                style={{ marginBottom: 16 }}
              />
              <Typography variant="subtitle2">
                {movement.kind === 'bank'
                  ? 'What goes into the bag'
                  : 'Notes handed over'}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginBottom: 8 }}
              >
                {movementCovers
                  ? 'Pre-filled from the envelopes you ticked. Change it if the bundle was broken into change — the pay-in slip has to match the bag, and it still has to come to the same money.'
                  : 'Count what actually left the drawer.'}
              </Typography>
              <NoteCounter
                denominations={denominations}
                counts={movement.counts}
                disabled={saving}
                target={
                  movementCovers ? Math.round(movement.envelopeTotal) : null
                }
                targetLabel="the envelopes"
                onChange={(next) =>
                  setMovement((prev) => ({ ...prev, counts: next }))
                }
              />
              <TextField
                label="Note (optional)"
                fullWidth
                size="small"
                variant="outlined"
                value={movement.note}
                onChange={(e) => {
                  const next = e.target.value
                  setMovement((prev) => ({ ...prev, note: next }))
                }}
                style={{ marginTop: 16, marginBottom: 12 }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const next = e.target.files?.[0] || null
                  setMovement((prev) => ({ ...prev, file: next }))
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
              >
                {movement.file ? 'Change proof' : 'Attach challan / photo'}
              </Button>
              <Typography
                variant="body2"
                color={movement.file ? 'textPrimary' : 'textSecondary'}
                style={{ marginTop: 6 }}
              >
                {movement.file
                  ? movement.file.name
                  : movement.kind === 'bank'
                  ? 'Photo or PDF of the bank challan — required.'
                  : 'Optional — a receipt or acknowledgement if there is one.'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovement(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!movementReady || saving}
            onClick={submitMovement}
          >
            {saving ? 'Saving…' : `Record ${inr(movementTotal)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Safe
